(() => {
  if (window.__elxHomeDiscoveryLoaded) return;
  window.__elxHomeDiscoveryLoaded = true;

  const ROOT_SELECTOR = '[data-elx-home-discovery-v3]';
  const reducedMotion = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

  const announce = (root, message) => {
    const status = root.querySelector('[data-elx-home-status]');
    if (status) status.textContent = message;
  };

  const copyText = async (value) => {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(value);
      return;
    }

    const textarea = document.createElement('textarea');
    textarea.value = value;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.append(textarea);
    textarea.select();
    const copied = document.execCommand('copy');
    textarea.remove();
    if (!copied) throw new Error('Unable to copy the sale code.');
  };

  const initCarousel = (carousel) => {
    const track = carousel.querySelector('[data-elx-home-carousel-track]');
    const previous = carousel.querySelector('[data-elx-home-carousel-prev]');
    const next = carousel.querySelector('[data-elx-home-carousel-next]');
    const dots = carousel.querySelector('[data-elx-home-carousel-dots]');
    const controls = carousel.querySelector('.elx-home-discovery-v3__carousel-controls');

    if (!track || track.dataset.elxHomeCarouselV3Ready === 'true') return;
    track.dataset.elxHomeCarouselV3Ready = 'true';

    let animationFrame;

    const maxScroll = () => Math.max(track.scrollWidth - track.clientWidth, 0);
    const cardPositions = () => {
      const max = maxScroll();
      if (max <= 2) return [0];

      const trackLeft = track.getBoundingClientRect().left;
      const positions = [0];

      // Both products and v3 categories are direct children of their tracks,
      // so each individual card is a valid horizontal scroll destination.
      [...track.children].forEach((card) => {
        const left = card.getBoundingClientRect().left - trackLeft + track.scrollLeft;
        positions.push(Math.min(max, Math.max(0, Math.round(left))));
      });

      return [...new Set(positions)].sort((left, right) => left - right);
    };

    const currentPage = () => {
      const positions = cardPositions();
      return positions.reduce((closest, position, index) => (
        Math.abs(position - track.scrollLeft) < Math.abs(positions[closest] - track.scrollLeft) ? index : closest
      ), 0);
    };

    const moveTo = (page) => {
      const positions = cardPositions();
      const bounded = Math.min(positions.length - 1, Math.max(0, page));
      track.scrollTo({
        left: positions[bounded],
        behavior: reducedMotion() ? 'auto' : 'smooth',
      });
    };

    const drawDots = () => {
      if (!dots) return;

      const positions = cardPositions();
      const total = positions.length;
      const active = currentPage();

      if (dots.childElementCount !== total) {
        dots.replaceChildren();
        positions.forEach((_, page) => {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'elx-home-discovery-v3__carousel-dot';
          dot.setAttribute('aria-label', `Go to slide ${page + 1} of ${total}`);
          dot.addEventListener('click', () => moveTo(page));
          dots.append(dot);
        });
      }

      dots.hidden = total <= 1;
      [...dots.children].forEach((dot, index) => {
        if (index === active) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    };

    const update = () => {
      animationFrame = undefined;
      const max = maxScroll();
      const total = cardPositions().length;
      const active = currentPage();
      const scrollable = max > 2;

      if (previous) previous.disabled = !scrollable || active === 0;
      if (next) next.disabled = !scrollable || active >= total - 1;
      if (controls) controls.hidden = !scrollable;
      carousel.classList.toggle('is-scrollable', scrollable);
      drawDots();
    };

    const scheduleUpdate = () => {
      if (animationFrame) return;
      animationFrame = window.requestAnimationFrame(update);
    };

    previous?.addEventListener('click', () => moveTo(currentPage() - 1));
    next?.addEventListener('click', () => moveTo(currentPage() + 1));
    track.addEventListener('scroll', scheduleUpdate, { passive: true });

    if ('ResizeObserver' in window) {
      new ResizeObserver(scheduleUpdate).observe(track);
    } else {
      window.addEventListener('resize', scheduleUpdate, { passive: true });
    }

    carousel.elxHomeCarouselV3Refresh = scheduleUpdate;
    update();
  };

  const initTabs = (root) => {
    const tabs = [...root.querySelectorAll('[data-elx-home-tab]')];
    const panels = [...root.querySelectorAll('[data-elx-home-panel]')];
    if (!tabs.length || root.dataset.elxHomeTabsV3Ready === 'true') return;
    root.dataset.elxHomeTabsV3Ready = 'true';

    const select = (key, focus = false) => {
      tabs.forEach((tab) => {
        const active = tab.dataset.elxHomeTab === key;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', String(active));
        tab.tabIndex = active ? 0 : -1;
        if (active && focus) tab.focus();
      });

      panels.forEach((panel) => {
        const active = panel.dataset.elxHomePanel === key;
        panel.hidden = !active;
        if (active) {
          panel.querySelectorAll('[data-elx-home-carousel]').forEach((carousel) => {
            carousel.elxHomeCarouselV3Refresh?.();
          });
        }
      });
    };

    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => select(tab.dataset.elxHomeTab));
      tab.addEventListener('keydown', (event) => {
        const last = tabs.length - 1;
        let nextIndex = null;

        if (event.key === 'ArrowRight') nextIndex = index === last ? 0 : index + 1;
        if (event.key === 'ArrowLeft') nextIndex = index === 0 ? last : index - 1;
        if (event.key === 'Home') nextIndex = 0;
        if (event.key === 'End') nextIndex = last;
        if (nextIndex === null) return;

        event.preventDefault();
        select(tabs[nextIndex].dataset.elxHomeTab, true);
      });
    });

    const initiallySelected = tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || tabs[0];
    select(initiallySelected.dataset.elxHomeTab);
  };

  const initCoupons = (root) => {
    root.querySelectorAll('[data-elx-home-coupon]').forEach((button) => {
      if (button.dataset.elxHomeCouponV3Ready === 'true') return;
      button.dataset.elxHomeCouponV3Ready = 'true';

      button.addEventListener('click', async () => {
        const code = button.dataset.elxHomeCoupon;
        if (!code) return;

        const originalLabel = button.getAttribute('aria-label') || `Copy sale code ${code}`;

        try {
          await copyText(code);
          await fetch(`/discount/${encodeURIComponent(code)}`, { credentials: 'same-origin' });
          button.classList.add('is-copied');
          button.setAttribute('aria-label', `Sale code ${code} copied`);
          announce(root, 'Sale code copied.');
          window.setTimeout(() => {
            button.classList.remove('is-copied');
            button.setAttribute('aria-label', originalLabel);
          }, 1600);
        } catch {
          announce(root, 'Unable to copy the sale code.');
        }
      });
    });
  };

  const initCartForms = (root) => {
    root.querySelectorAll('[data-elx-home-add-form]').forEach((form) => {
      if (form.dataset.elxHomeAddV4Ready === 'true') return;
      form.dataset.elxHomeAddV4Ready = 'true';

      form.addEventListener('submit', async (event) => {
        event.preventDefault();

        const button = form.querySelector('[data-elx-home-add-button]');
        const label = button?.querySelector('[data-elx-home-add-label]');
        const original = label?.textContent || 'Add to Cart';
        const variantId = new FormData(form).get('id');
        if (!variantId || button?.disabled) return;

        if (button) {
          button.disabled = true;
          button.setAttribute('aria-busy', 'true');
        }
        if (label) label.textContent = 'Adding…';

        try {
          const addItemsToCart = window.ElxCartActions?.addItemsToCart;
          if (typeof addItemsToCart !== 'function') throw new Error('Shopping cart is still loading. Please try again.');

          await addItemsToCart({
            items: [{ id: variantId, quantity: 1 }],
            sourceId: root.id,
            source: 'elx-home-discovery',
          });
          announce(root, 'Added to cart.');
        } catch (error) {
          announce(root, error?.message || 'Unable to add to cart.');
        } finally {
          if (button) {
            button.disabled = false;
            button.removeAttribute('aria-busy');
          }
          if (label) label.textContent = original;
        }
      });
    });
  };

  const rootsIn = (scope) => {
    if (!scope?.querySelectorAll) return [];
    const roots = [...scope.querySelectorAll(ROOT_SELECTOR)];
    if (scope.matches?.(ROOT_SELECTOR)) roots.unshift(scope);
    return roots;
  };

  const init = (scope = document) => {
    rootsIn(scope).forEach((root) => {
      initTabs(root);
      root.querySelectorAll('[data-elx-home-carousel]').forEach(initCarousel);
      initCoupons(root);
      initCartForms(root);
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
